import { createAsyncThunk } from "@reduxjs/toolkit";
import $api from "../http";
import { toApiError } from "../http/types";

export const requestServise = {
  post: <TResponse = unknown, TBody = unknown>(name: string, url: string) => {
    return createAsyncThunk<TResponse, TBody, { rejectValue: string }>(
      name,
      async (body, { rejectWithValue }) => {
        try {
          const response = await $api.post<TResponse>(
            url,
            JSON.stringify(body),
          );
          return response.data;
        } catch (error) {
          const apiError = toApiError(error);
          return rejectWithValue(apiError.message);
        }
      },
    );
  },
  noBodyPost: <TResponse = unknown>(name: string, url: string) => {
    return createAsyncThunk<TResponse, void, { rejectValue: string }>(
      name,
      async (_, { rejectWithValue }) => {
        try {
          const response = await $api.post<TResponse>(url);
          return response.data;
        } catch (error) {
          const apiError = toApiError(error);
          return rejectWithValue(apiError.message);
        }
      },
    );
  },
  get: <TResponse = unknown, TQuery = void>(
    name: string,
    url: string | ((query: TQuery) => string),
  ) => {
    return createAsyncThunk<TResponse, TQuery, { rejectValue: string }>(
      name,
      async (query, { rejectWithValue }) => {
        try {
          const fullUrl =
            typeof url === "function"
              ? url(query)
              : query
                ? `${url}?${query}`
                : url;
          const response = await $api.get<TResponse>(fullUrl);
          return response.data;
        } catch (error) {
          const apiError = toApiError(error);
          return rejectWithValue(apiError.message);
        }
      },
    );
  },
  put: <TResponse = unknown, TBody = unknown>(
    name: string,
    url: string | ((body: TBody) => string),
  ) => {
    return createAsyncThunk<TResponse, TBody, { rejectValue: string }>(
      name,
      async (body, { rejectWithValue }) => {
        try {
          const fullUrl = typeof url === "function" ? url(body) : url;
          const response = await $api.put<TResponse>(
            fullUrl,
            JSON.stringify(body),
          );
          return response.data;
        } catch (error) {
          const apiError = toApiError(error);
          return rejectWithValue(apiError.message);
        }
      },
    );
  },
  patch: <TResponse = unknown, TBody = unknown>(name: string, url: string) => {
    return createAsyncThunk<TResponse, TBody, { rejectValue: string }>(
      name,
      async (body, { rejectWithValue }) => {
        try {
          const response = await $api.patch<TResponse>(
            url,
            JSON.stringify(body),
          );
          return response.data;
        } catch (error) {
          const apiError = toApiError(error);
          return rejectWithValue(apiError.message);
        }
      },
    );
  },
  delete: <TResponse = unknown, TQuery = void>(name: string, url: string) => {
    return createAsyncThunk<TResponse, TQuery | void, { rejectValue: string }>(
      name,
      async (query, { rejectWithValue }) => {
        try {
          const fullUrl = query ? `${url}?${query}` : url;
          const response = await $api.delete<TResponse>(fullUrl);
          return response.data;
        } catch (error) {
          const apiError = toApiError(error);
          return rejectWithValue(apiError.message);
        }
      },
    );
  },
  deleteWithBody: <TResponse = unknown, TBody = unknown>(
    name: string,
    url: string | ((body: TBody) => string),
  ) => {
    return createAsyncThunk<TResponse, TBody, { rejectValue: string }>(
      name,
      async (body, { rejectWithValue }) => {
        try {
          const fullUrl = typeof url === "function" ? url(body) : url;
          const response = await $api.delete<TResponse>(fullUrl, {
            data: body,
          });
          return response.data;
        } catch (error) {
          const apiError = toApiError(error);
          return rejectWithValue(apiError.message);
        }
      },
    );
  },
};
